import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Excel/Sheets-style resizable column hook for tables that use the
 * <table class="ox-table"> markup.
 *
 *   const { gripRef, widths } = useResizableColumns("deals-v1", 15);
 *
 *   <thead>
 *     <tr>
 *       <th ref={gripRef(0)} style={widthStyle(0, "180px")}>CLIENT</th>
 *       ...
 *
 * Widths are persisted in localStorage keyed by `tableId` so users see the
 * same layout next session. Drag the small grip on the right edge of any
 * header to resize.
 */
export default function useResizableColumns(tableId, columnCount, opts = {}) {
  const { minWidth = 60, defaults = {} } = opts;
  const storageKey = `cc-colwidths-${tableId}`;
  const headRefs = useRef([]);

  const [widths, setWidths] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch (_) { /* noop */ }
    return defaults;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch (_) { /* noop */ }
  }, [widths, storageKey]);

  const setRef = useCallback((idx) => (el) => {
    headRefs.current[idx] = el;
  }, []);

  const widthStyle = useCallback(
    (idx, fallback) => {
      const w = widths[idx];
      if (w) return { width: `${w}px`, minWidth: `${w}px` };
      if (fallback) return { width: fallback, minWidth: fallback };
      return {};
    },
    [widths],
  );

  /**
   * The drag grip rendered inside <th> content. Mouse-down stores the start
   * position + start width, mousemove updates state live (smooth), mouseup
   * commits + writes to localStorage. DOUBLE-CLICK = auto-fit to the
   * widest cell in that column (Excel/Sheets behavior).
   */
  const Grip = useCallback(
    function Grip({ index }) {
      const onMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const th = headRefs.current[index];
        if (!th) return;
        const startX = e.clientX;
        const startWidth = th.getBoundingClientRect().width;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";

        const onMove = (ev) => {
          const delta = ev.clientX - startX;
          const next = Math.max(minWidth, Math.round(startWidth + delta));
          setWidths((prev) => ({ ...prev, [index]: next }));
        };
        const onUp = () => {
          document.body.style.cursor = "";
          document.body.style.userSelect = "";
          window.removeEventListener("mousemove", onMove);
          window.removeEventListener("mouseup", onUp);
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
      };

      const onDoubleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const th = headRefs.current[index];
        if (!th) return;
        const table = th.closest("table");
        if (!table) {
          setWidths((prev) => {
            const next = { ...prev };
            delete next[index];
            return next;
          });
          return;
        }
        // Find every td in this column index and measure its scrollWidth.
        const rows = table.querySelectorAll("tbody tr");
        let widest = th.scrollWidth + 16;
        rows.forEach((row) => {
          const cell = row.children[index];
          if (cell) {
            const probe = cell.firstElementChild || cell;
            const w = (probe.scrollWidth || cell.scrollWidth) + 24;
            if (w > widest) widest = w;
          }
        });
        const next = Math.max(minWidth, Math.min(800, Math.round(widest)));
        setWidths((prev) => ({ ...prev, [index]: next }));
      };

      return (
        <span
          onMouseDown={onMouseDown}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={onDoubleClick}
          className="col-resize-grip"
          title="Drag to resize · Double-click to auto-fit"
          aria-label={`Resize column ${index + 1}`}
        />
      );
    },
    [minWidth],
  );

  const reset = useCallback(() => setWidths({}), []);

  return { setRef, widthStyle, Grip, reset, widths };
}
